import { Controller } from "stimulus"

// links.* variables are intentionally excluded — they appear as URLs inside
// markdown link syntax [text]({{links.yes_response}}) and must stay as raw
// Liquid tags or the markdown round-trip escapes the surrounding brackets.
const VAR_MAP = [
  ['recipient.first_name',           'NAME'],
  ['recipient.last_name',            'LAST NAME'],
  ['recipient.full_name',            'FULL NAME'],
  ['recipient.email',                'RECIPIENT EMAIL'],
  ['recipient.location',             'LOCATION'],
  ['recipient.time_zone',            'TIME ZONE'],
  ['round.start_date',               'DATE'],
  ['round.matching_style',           'MATCHING STYLE'],
  ['program.name',                   'PROGRAM'],
  ['program.email_label',            'PROGRAM LABEL'],
  ['other_participant.first_name',   'PARTNER NAME'],
  ['other_participant.last_name',    'PARTNER LAST NAME'],
  ['other_participant.full_name',    'PARTNER FULL NAME'],
  ['other_participant.email',        'PARTNER EMAIL'],
  ['other_participant.location',     'PARTNER LOCATION'],
  ['other_participant.time_zone',    'PARTNER TIME ZONE'],
  ['participant_1.first_name',       'P1 NAME'],
  ['participant_1.full_name',        'P1 FULL NAME'],
  ['participant_1.email',            'P1 EMAIL'],
  ['participant_2.first_name',       'P2 NAME'],
  ['participant_2.full_name',        'P2 FULL NAME'],
  ['participant_2.email',            'P2 EMAIL'],
  ['prompt.title',                   'PROMPT TITLE'],
  ['prompt.description',             'PROMPT DESC'],
  ['prompt.link',                    'PROMPT LINK'],
  ['has_profile',                    'HAS PROFILE'],
]

// Matches {{var}} and {{ var }} (Liquid allows spaces inside braces)
function toDisplay(text) {
  let s = text
  for (const [key, label] of VAR_MAP) {
    const escapedKey = key.split('.').join('\\.')
    s = s.replace(new RegExp('\\{\\{\\s*' + escapedKey + '\\s*\\}\\}', 'g'), '[' + label + ']')
  }
  return s
}

function fromDisplay(text) {
  let s = text
  // Toast UI Editor URL-encodes {{ and }} when they appear inside link hrefs.
  // Decode them back before saving so Liquid vars in links are never corrupted.
  s = s.replace(/%7B%7B/gi, '{{').replace(/%7D%7D/gi, '}}')
  for (const [key, label] of VAR_MAP) {
    // Handle both [LABEL] and \[LABEL\] (editor sometimes escapes brackets)
    s = s.split('[' + label + ']').join('{{' + key + '}}')
    s = s.split('\\[' + label + '\\]').join('{{' + key + '}}')
  }
  return s
}

export default class extends Controller {
  static targets = ["body", "container", "status", "subject"]

  async connect() {
    this.cancelled = false
    this.dirty = false

    // Transform subject field to friendly labels on load
    if (this.hasSubjectTarget) {
      this.subjectTarget.value = toDisplay(this.subjectTarget.value)
    }

    const { default: Editor } = await import('@toast-ui/editor')

    if (this.cancelled) return

    this.editor = new Editor({
      el: this.containerTarget,
      initialEditType: 'wysiwyg',
      hideModeSwitch: true,
      height: '420px',
      initialValue: toDisplay(this.bodyTarget.value),
      toolbarItems: [
        ['heading', 'bold', 'italic', 'strike'],
        ['ul', 'ol'],
        ['link'],
      ]
    })

    this.editor.on('change', () => {
      this.bodyTarget.value = fromDisplay(this.editor.getMarkdown())
      this.dirty = true
      if (this.hasStatusTarget) this.statusTarget.textContent = 'Unsaved changes'
    })

    this.boundBeforeUnload = (event) => {
      if (this.dirty) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this.boundBeforeUnload)
  }

  insertVar(event) {
    const key = event.currentTarget.dataset.liqKey
    if (!key) return
    const entry = VAR_MAP.find(([k]) => k === key)
    // For mapped vars use [LABEL], for unmapped (links.*) use raw {{key}}
    const text = entry ? '[' + entry[1] + ']' : '{{' + key + '}}'

    // Insert into subject if it is focused
    if (this.hasSubjectTarget && document.activeElement === this.subjectTarget) {
      const el = this.subjectTarget
      const start = el.selectionStart
      const end = el.selectionEnd
      el.value = el.value.substring(0, start) + text + el.value.substring(end)
      el.selectionStart = el.selectionEnd = start + text.length
      return
    }

    if (!this.editor) return
    this.editor.insertText(text)
    this.bodyTarget.value = fromDisplay(this.editor.getMarkdown())
    this.dirty = true
    if (this.hasStatusTarget) this.statusTarget.textContent = 'Unsaved changes'
  }

  syncBeforeSubmit() {
    if (this.hasSubjectTarget) {
      this.subjectTarget.value = fromDisplay(this.subjectTarget.value)
    }
    if (this.editor) {
      this.bodyTarget.value = fromDisplay(this.editor.getMarkdown())
    }
  }

  disconnect() {
    this.cancelled = true
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
    if (this.boundBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundBeforeUnload)
    }
  }
}
